'use client';

import { FC, HTMLAttributes, useLayoutEffect } from 'react';
import classNames from 'classnames';

import {
  useCookies,
  useFeaturedFlag,
  useIsServerSide,
  useSelectedStore,
} from '@/hooks';
import { useEcomStoreSelector } from '@/data';
import { EnumCustomerType as CustomerType } from '@/types';
import { CookiesNames } from '@/constants';

import styles from './MedRecSwitch.module.scss';

const MedRecSwitch: FC<{ compact?: boolean }> = ({ compact }) => {
  const { setCookieValue } = useCookies();
  const { isMedAndRecSelectionActive } = useFeaturedFlag();
  const { customer_type, line_items, setState } = useEcomStoreSelector([
    'customer_type',
    'line_items',
    'medical_id',
  ]);
  const { isServerSide } = useIsServerSide();
  const { store } = useSelectedStore();

  useLayoutEffect(() => {
    if (isMedAndRecSelectionActive) {
      const hasSelectableMode =
        customer_type === CustomerType.ADULT ||
        customer_type === CustomerType.MEDICAL;

      if (!hasSelectableMode) {
        setState({ customer_type: CustomerType.ADULT });
      }
      return;
    }

    if (store?.short_name && customer_type !== CustomerType.NOT_SET) {
      setState({ customer_type: CustomerType.NOT_SET });
      setCookieValue(CookiesNames._CUSTOMER_TYPE, CustomerType.NOT_SET);
    }
  }, [isMedAndRecSelectionActive, store?.short_name, customer_type]);

  const handleRecSelection = () => {
    if (line_items.length) {
      setState({ next_customer_type: CustomerType.ADULT });
      return;
    }
    setState({ customer_type: CustomerType.ADULT });
  };

  const handleMedSelection = () => {
    setState({ next_customer_type: CustomerType.MEDICAL });
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useLayoutEffect(() => {
    if (!isMedAndRecSelectionActive) return;

    setCookieValue(
      CookiesNames._CUSTOMER_TYPE,
      customer_type === CustomerType.ALL ? 'BOTH' : customer_type,
    );
  }, [customer_type]);

  if (!isMedAndRecSelectionActive) {
    return null;
  }

  return (
    <div
      className={classNames(styles.switch, {
        [styles.switch__alternate_header_variation]: compact,
      })}
    >
      <div className={classNames(styles.switch_container)}>
        {!isServerSide && (
          <div
            className={classNames(styles.switch__selected_background, {
              [styles.right_checked]: customer_type === CustomerType.MEDICAL,
              [styles.switch__selected_background__alternate_header_variation]:
                compact,
            })}
          />
        )}
        <SwitchButton
          onClick={handleRecSelection}
          selected={!isServerSide && customer_type === CustomerType.ADULT}
          text="REC"
        />
        <SwitchButton
          onClick={handleMedSelection}
          selected={!isServerSide && customer_type === CustomerType.MEDICAL}
          text="MED"
        />
      </div>
    </div>
  );
};

interface SwitchButtonProps extends HTMLAttributes<HTMLButtonElement> {
  text: string;
  selected: boolean;
  compact?: boolean;
}

const SwitchButton: FC<SwitchButtonProps> = ({
  text,
  selected,
  compact,
  ...props
}) => {
  return (
    <button
      className={classNames(styles.switch__button, {
        [styles.switch__button_selected]: selected,
        [styles.switch__button__alternate_header_variation]: compact,
      })}
      {...props}
      aria-label={selected ? `${text} type selected` : `Select ${text} type`}
      aria-pressed={selected}
    >
      {text}
    </button>
  );
};

export { MedRecSwitch };
